import { createContext, useContext, useMemo, useState, useCallback, useEffect } from 'react';
import equals from 'ramda/es/equals';
import { useRecoilState } from 'recoil';
import type { SortColumn, SortOrder } from 'generated/graphql';

import { FF, useFeatureFlagEnabled } from 'modules/feature-flags';
import { SORTABLE_WORKLIST_COLUMN_MAP } from 'config/constants';
import { activeCustomWorklistIDsState } from '../state';
import { useCustomWorklists } from '../hooks/useCustomWorklists';
import {
  useCustomWorklistSortingPreferences,
  useCustomWorklistPreferences,
} from '../hooks/useCustomWorklistPreferences';
import { useCustomWorklistPreferenceUpdates } from '../hooks/useCustomWorklistPreferenceUpdates';
import { useWorklistURLParams } from '../useWorklistURLParams';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { logger } from 'modules/logger';
import { telemetryContextFor } from 'modules/Apollo/types';
import { useHasWorklistFilterSortPermission } from '../hooks/useHasWorklistFilterSortPermission';
import { DEFAULT_SORT_COLUMNS, DEFAULT_SORT_ORDERS } from '../../WorklistFilters/constants';

type WorklistSortContextBag = {
  sortColumns: Array<SortColumn>;
  sortOrders: Array<SortOrder>;
  onSortChange: (columnIds: string[], orders: Array<SortOrder>) => void;
};

type WorklistSortContextProviderProps = {
  children: React.ReactNode;
  overrideURLSearchParams?: Nullable<URLSearchParams>;
  skipWorklistQueries?: boolean;
};

const WorklistSortContext = createContext<WorklistSortContextBag | null>(null);

// No-op onSortChange so nothing changes or gets saved while sorting is locked.
const LOCKED_SORT_CONTEXT_VALUE: WorklistSortContextBag = {
  sortColumns: DEFAULT_SORT_COLUMNS,
  sortOrders: DEFAULT_SORT_ORDERS,
  onSortChange: () => {},
};

export function WorklistSortContextProvider({
  children,
  overrideURLSearchParams,
  skipWorklistQueries = false,
}: WorklistSortContextProviderProps): React.ReactElement {
  const [isRefactorEnabled] = useFeatureFlagEnabled(FF.WORKLIST_CONTEXT_REFACTOR);
  const canFilterAndSort = useHasWorklistFilterSortPermission();

  const [activeCustomWorklistIDs] = useRecoilState(activeCustomWorklistIDsState);
  const { isPublicOrCombinedWorklist, privateWorklists } = useCustomWorklists({
    queryOptions: {
      context: APOLLO_QUERY_CONTEXT,
      skip: skipWorklistQueries,
    },
  });
  const { data: currentUserData } = useCurrentUser({
    context: APOLLO_QUERY_CONTEXT,
    skip: skipWorklistQueries,
  });

  const isPublicOrCombined = isPublicOrCombinedWorklist(activeCustomWorklistIDs);

  const { sortColumn: urlSortColumns, sortOrder: urlSortOrders } =
    useWorklistURLParams(overrideURLSearchParams);

  const customWorklistSortingPreferences = useCustomWorklistSortingPreferences({
    worklistIds: activeCustomWorklistIDs,
    isPublicOrCombined,
    context: APOLLO_QUERY_CONTEXT,
    skipQueries: skipWorklistQueries,
  });

  const customWorklistPreferences = useCustomWorklistPreferences({
    worklistIds: activeCustomWorklistIDs,
    isPublicOrCombined,
    context: APOLLO_QUERY_CONTEXT,
    skipQueries: skipWorklistQueries,
  });

  const shouldUseCustomWorklistPreferences = useMemo(() => {
    return activeCustomWorklistIDs.length > 0;
  }, [activeCustomWorklistIDs.length]);

  // use custom worklist preferences if available, otherwise use URL params
  const initialSortColumns = useMemo(() => {
    if (!isRefactorEnabled) {
      return [];
    }
    if (shouldUseCustomWorklistPreferences && customWorklistSortingPreferences?.sortColumns) {
      return customWorklistSortingPreferences.sortColumns;
    }
    return urlSortColumns ?? [];
  }, [
    isRefactorEnabled,
    shouldUseCustomWorklistPreferences,
    customWorklistSortingPreferences?.sortColumns,
    urlSortColumns,
  ]);

  const initialSortOrders = useMemo(() => {
    if (!isRefactorEnabled) {
      return [];
    }
    if (shouldUseCustomWorklistPreferences && customWorklistSortingPreferences?.sortOrders) {
      return customWorklistSortingPreferences.sortOrders;
    }
    return urlSortOrders ?? [];
  }, [
    isRefactorEnabled,
    shouldUseCustomWorklistPreferences,
    customWorklistSortingPreferences?.sortOrders,
    urlSortOrders,
  ]);

  const [sortColumns, setSortColumns] = useState<Array<SortColumn>>(initialSortColumns);
  const [sortOrders, setSortOrders] = useState<Array<SortOrder>>(initialSortOrders);

  const { updateCustomWorklistSortingSettings } = useCustomWorklistPreferenceUpdates({
    activeCustomWorklistIDs,
    privateWorklists,
    worklistSettings: currentUserData?.me?.worklistSettings,
    customWorklistSettings: customWorklistPreferences,
    userSmid: currentUserData?.me?.id,
    skipQueries: skipWorklistQueries,
    context: APOLLO_QUERY_CONTEXT,
  });

  // Sync sort state with URL params or custom worklist preferences when they change
  useEffect(() => {
    if (!isRefactorEnabled) {
      return;
    }

    const shouldApplyUrlSorting =
      !shouldUseCustomWorklistPreferences &&
      ((urlSortColumns?.length ?? 0) > 0 || (urlSortOrders?.length ?? 0) > 0);

    if (shouldApplyUrlSorting) {
      setSortColumns(urlSortColumns ?? []);
      setSortOrders(urlSortOrders ?? []);
    } else if (shouldUseCustomWorklistPreferences && customWorklistSortingPreferences) {
      setSortColumns(customWorklistSortingPreferences.sortColumns);
      setSortOrders(customWorklistSortingPreferences.sortOrders);
    }
  }, [
    isRefactorEnabled,
    customWorklistSortingPreferences,
    shouldUseCustomWorklistPreferences,
    urlSortColumns,
    urlSortOrders,
  ]);

  const onSortChange = useCallback(
    (columnIds: string[], orders: Array<SortOrder>) => {
      if (!isRefactorEnabled) {
        return;
      }

      // Map column IDs to SortColumn enum values
      const mappedColumns: Array<SortColumn> = columnIds.map(
        (id) =>
          SORTABLE_WORKLIST_COLUMN_MAP[
            id as keyof typeof SORTABLE_WORKLIST_COLUMN_MAP
          ] as SortColumn
      );

      const changedColumns = !equals(mappedColumns, sortColumns);
      const changedOrders = !equals(orders, sortOrders);

      if (changedColumns) {
        setSortColumns(mappedColumns);
      }

      if (changedOrders) {
        setSortOrders(orders);
      }

      logger.info(
        `[WorklistSortContext] Sort changed${activeCustomWorklistIDs.length > 0 ? ' for custom worklist: ' + activeCustomWorklistIDs.join(', ') : ''}`,
        {
          previousColumns: sortColumns,
          previousOrders: sortOrders,
          newColumns: mappedColumns,
          newOrders: orders,
          customWorklistIDs: activeCustomWorklistIDs,
          willSavePreferences:
            shouldUseCustomWorklistPreferences && (changedColumns || changedOrders),
        }
      );

      if ((changedColumns || changedOrders) && shouldUseCustomWorklistPreferences) {
        updateCustomWorklistSortingSettings(mappedColumns, orders);
      }
    },
    [
      isRefactorEnabled,
      sortColumns,
      sortOrders,
      shouldUseCustomWorklistPreferences,
      updateCustomWorklistSortingSettings,
      activeCustomWorklistIDs,
    ]
  );

  const value = useMemo(() => {
    // Without the permission, always use the default sort. Saved and URL sorts are kept, just not used.
    if (!canFilterAndSort) {
      return LOCKED_SORT_CONTEXT_VALUE;
    }
    return {
      sortColumns,
      sortOrders,
      onSortChange,
    };
  }, [canFilterAndSort, sortColumns, sortOrders, onSortChange]);

  return <WorklistSortContext.Provider value={value}>{children}</WorklistSortContext.Provider>;
}

export function useWorklistSortContext(): WorklistSortContextBag {
  const context = useContext(WorklistSortContext);
  if (context == null) {
    throw new Error('useWorklistSortContext must be used within a WorklistSortContextProvider.');
  }
  return context;
}

export type { WorklistSortContextBag };

WorklistSortContextProvider.displayName = 'WorklistSortContextProvider';

const APOLLO_QUERY_CONTEXT = telemetryContextFor(WorklistSortContextProvider);
